import { rest } from 'msw';

const URL_BASE = `${process.env.REACT_APP_BASE_URL_SUBSCRIPTIONS_API}/`;

const PAYMENT_METHOD_CARD = { uuid: 'pm-card', methodType: 'CARD', methodDetail: '1234' };
const PAYMENT_METHOD_SPEI = { uuid: 'pm-spei', methodType: 'SPEI', methodDetail: '' };

type MockRow = {
  uuid: string;
  subscriptionUuid: string;
  subscriptionName: string;
  paymentDate: string;
  concept: string;
  status: string;
  total: string;
  paymentMethod: typeof PAYMENT_METHOD_CARD;
  invoiceData: {
    folio: string;
    pdfUrl: string | null;
    xmlUrl: string | null;
    status: string;
    errorMessage: string | null;
  } | null;
};

const buildRow = (row: MockRow) => ({
  uuid: row.uuid,
  orgUuid: 'org-001',
  subscriptionUuid: row.subscriptionUuid,
  subscriptionName: row.subscriptionName,
  invoiceDataUuid: 'invoice-data-001',
  paymentMethodUuid: row.paymentMethod.uuid,
  paymentDate: row.paymentDate,
  concept: row.concept,
  status: row.status,
  total: row.total,
  totalInvoice: row.total,
  currencyCode: 'MXN',
  paymentMethod: row.paymentMethod,
  invoiceData: row.invoiceData,
  concepts: [],
  createdAt: row.paymentDate,
  updatedAt: row.paymentDate,
});

const issued = (folio: string) => ({
  folio,
  pdfUrl: `https://example.com/${folio}.pdf`,
  xmlUrl: `https://example.com/${folio}.xml`,
  status: 'ISSUED',
  errorMessage: null,
});

/**
 * A grouped charge and a per-store one in the same page, which is what the screen has to read
 * once a customer toggles `group_invoice`: the identity cell alternates between the subscription
 * name and the store name under a header that always says Suscripción.
 */
const ROWS: MockRow[] = [
  {
    uuid: 'pay-004',
    subscriptionUuid: 'sub-002',
    subscriptionName: 'Parrot Norte',
    paymentDate: '2026-04-10',
    concept: 'Pago de suscripción',
    status: 'PAID',
    total: '5237.00',
    paymentMethod: PAYMENT_METHOD_CARD,
    invoiceData: issued('A0004'),
  },
  {
    uuid: 'pay-003:store-001',
    subscriptionUuid: 'sub-001',
    subscriptionName: 'Parrot Cuauhtémoc',
    paymentDate: '2026-04-10',
    concept: 'Pago de suscripción',
    status: 'PAID',
    total: '5817.00',
    paymentMethod: PAYMENT_METHOD_SPEI,
    invoiceData: issued('A0003'),
  },
  {
    uuid: 'pay-003:store-002',
    subscriptionUuid: 'sub-001',
    subscriptionName: 'Parrot Narvarte',
    paymentDate: '2026-04-10',
    concept: 'Pago de suscripción',
    status: 'PAID',
    total: '4657.00',
    paymentMethod: PAYMENT_METHOD_SPEI,
    invoiceData: issued('A0002'),
  },
  {
    uuid: 'pay-002',
    subscriptionUuid: 'sub-001',
    subscriptionName: 'Región Centro',
    paymentDate: '2026-03-10',
    concept: 'Pago de suscripción',
    status: 'PAID',
    total: '9411.84',
    paymentMethod: PAYMENT_METHOD_SPEI,
    invoiceData: issued('A0001'),
  },
  {
    uuid: 'pay-001',
    subscriptionUuid: 'sub-001',
    subscriptionName: 'Región Centro',
    paymentDate: '2026-02-10',
    concept: 'Factura anticipada',
    status: 'PENDING',
    total: '9411.84',
    paymentMethod: PAYMENT_METHOD_SPEI,
    invoiceData: null,
  },
];

/**
 * Mock of `GET /v1/payments` returning one row per issued invoice instead of nesting the
 * per-store ones under the charge. Stands in for the backend change described in spec 011-01
 * §7.1.1; delete it once the API emits this shape. Enable with REACT_APP_ENABLE_MOCK_SERVER=true.
 */
export const getFlattenedPaymentHistory = rest.get(`${URL_BASE}v1/payments`, (req, res, ctx) => {
  const blockUuid = req.url.searchParams.get('block_uuid');
  const page = Number(req.url.searchParams.get('page') ?? 1);
  const pageSize = Number(req.url.searchParams.get('pageSize') ?? 50);

  // Any block uuid maps to sub-001: the real detail page passes the uuid the API gave it, which
  // never matches the fake ones here.
  const rows = blockUuid ? ROWS.filter((row) => row.subscriptionUuid === 'sub-001') : ROWS;
  const start = (page - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);
  const hasNext = start + pageSize < rows.length;

  return res(
    ctx.status(200),
    ctx.json({
      data: pageRows.map(buildRow),
      pagination: {
        next: hasNext ? `/v1/payments?page=${page + 1}&pageSize=${pageSize}` : null,
        previous: page > 1 ? `/v1/payments?page=${page - 1}&pageSize=${pageSize}` : null,
      },
      datetime: new Date().toISOString(),
    }),
  );
});
